import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserService from "../../../utils/api/users";
import SupplierService from "../../../utils/api/supplierdirectory";
import { Avatar, Button, Skeleton, Upload, message, Input, Modal, Table, Col, Row } from "antd";
import {
  UserOutlined,
  HomeOutlined,
  CreditCardOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  EditOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  ApartmentOutlined,
  PercentageOutlined,
  GlobalOutlined,
  IdcardOutlined,
  PayCircleOutlined
} from '@ant-design/icons';

import "./ClientInfo.scss";
import CreateSupplierForm from "../../../pages/Suppliers/createSupplierForm";

const InfoItem = ({ icon, text, isEditing, value, onChange }) => (
  <div className="info-item">
    {icon}
    {isEditing ? (
      <Input value={value} onChange={onChange} />
    ) : (
      <span>{text || "No disponible"}</span>
    )}
  </div>
);


const getCurrentUserSession = () => {
  let role = 0;
  let id = null;

  const roleStr = localStorage.getItem('role');
  if (roleStr) {
    try {
      role = JSON.parse(roleStr);
    } catch (err) {
      role = roleStr;
    }
  }

  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const parsedUser = JSON.parse(userStr);
      id = parsedUser?.id ?? null;
    } catch (err) {
      console.error('Error parsing user from localStorage:', err);
    }
  }

  return { role, id };
};

const updateUserInLocalStorage = (userData) => {
  try {
    localStorage.setItem('user', JSON.stringify(userData));
  } catch (err) {
    console.error('Error updating user in localStorage:', err);
  }
};

export default function ClientInfo({ profile, onProfileUpdate, profileSupplier }) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(profile || {});

  // modal to show supplier contacts / data
  const [isContactsModalVisible, setIsContactsModalVisible] = useState(false);

  // editable supplier state
  const [editedSupplier, setEditedSupplier] = useState(profileSupplier || {});
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState(null);

  const [isBannerModalVisible, setIsBannerModalVisible] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  const [isLogoModalVisible, setIsLogoModalVisible] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [fiscalFile, setFiscalFile] = useState(null);
  const [fiscalUploading, setFiscalUploading] = useState(false);

  // supplier file replacement states
  const [catalogFile, setCatalogFile] = useState(null);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const [certFiles, setCertFiles] = useState([]);
  const [certUploading, setCertUploading] = useState(false);




  useEffect(() => {
    setEditedProfile(profile || {});

  }, [profile]);

  useEffect(() => {
    setEditedSupplier(profileSupplier || {});
  }, [profileSupplier]);

  const handleInputChange = (e, field) => {
    setEditedProfile({ ...editedProfile, [field]: e.target.value });
  };

  const handleOpenCertificate = async (cert) => {
    if (!cert) return message.error('Certificado no disponible');
    if (cert.fileUrl && cert.fileUrl.includes('?')) {
      window.open(cert.fileUrl, '_blank');
      return;
    }

    try {
      if (!profileSupplier || !profileSupplier.id) return message.error('Certificado no disponible');
      const resp = await SupplierService.GetSupplierById(profileSupplier.id);
      const fresh = resp.data;

      const found = (fresh.certificates || []).find(c => c.id === cert.id);

      const url = found?.fileUrl || found?.path || null;
      if (url) {
        message.info('Abriendo certificado...');
        window.open(url, '_blank');
      } else {
        message.error('No se pudo obtener el certificado.');
      }
    } catch (err) {
      console.error('Error fetching signed certificate url', err);
      message.error('No se pudo obtener el certificado.');
    }
  };

  const handleEditToggle = () => {

    if (!isEditing && isSupplier) {
      if (profileSupplier && profileSupplier.id) {

        setEditingSupplierId(profileSupplier.id);
        setOpenCreateModal(true);
        return;
      }
    }

    if (isEditing) {
      setEditedProfile(profile);
      setEditedSupplier(profileSupplier);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    const payload = {
      userId: profile.id,
      phone: editedProfile.phone,
      aboutMe: editedProfile.aboutMe
    };

    setIsEditing(false);

    UserService.UpdateUser(payload)
      .then(async () => {
        message.success("Perfil actualizado con éxito.");

        if (profileSupplier && profileSupplier.id) {
          const supplierPayload = {
            bussiness: editedSupplier.bussiness,
            description: editedSupplier.description,
            category: editedSupplier.category,
            subcategory: editedSupplier.subcategory,
            country: editedSupplier.country,
            state: editedSupplier.state,
            city: editedSupplier.city,
            street: editedSupplier.street,
            postal_code: editedSupplier.postal_code || null,
            website: editedSupplier.website || null,
            rfc: editedSupplier.rfc,
            iva: editedSupplier.iva,
            porcentage_iva: editedSupplier.porcentage_iva,
            credit_days: editedSupplier.credit_days || editedSupplier.creditDays || 0
          };

          try {
            await SupplierService.EditSupplier(profileSupplier.id, supplierPayload);
            message.success('Proveedor actualizado con éxito.');
          } catch (err) {
            console.error('Error updating supplier', err);
            message.error('Error al actualizar datos del proveedor.');
          }
        }

        if (onProfileUpdate) await onProfileUpdate();
      })
      .catch(err => {
        const errorMessage = err.response?.data?.message || "Hubo un error al actualizar el perfil.";
        message.error(errorMessage);
      });
  };

  const handleSupplierChange = (field, value) => {
    setEditedSupplier(prev => ({ ...prev, [field]: value }));
  };

  const handleBannerUpload = () => {
    if (!bannerFile) return message.error('Por favor, selecciona una imagen.');

    const reader = new FileReader();
    reader.readAsDataURL(bannerFile);
    reader.onload = () => {
      const payload = { userId: profile.id, banner: reader.result };

      setBannerUploading(true);
      UserService.ChangeBanner(payload)
        .then((response) => {
          message.success('Banner actualizado exitosamente.');
          setIsBannerModalVisible(false);
          setBannerFile(null);

          if (response && response.data) {
            updateUserInLocalStorage(response.data);
          }

          if (onProfileUpdate) onProfileUpdate();
        })
        .catch(err => {
          const errorMessage = err.response?.data?.message || 'Error al subir el banner.';
          message.error(errorMessage);
        })
        .finally(() => setBannerUploading(false));
    };
    reader.onerror = () => message.error('Error al leer el archivo de imagen.');
  };

  const handleLogoUpload = () => {
    if (!logoFile) return message.error('Por favor, selecciona una imagen.');

    const reader = new FileReader();
    reader.readAsDataURL(logoFile);
    reader.onload = () => {
      const payload = { userId: profile.id, logo: reader.result };

      setLogoUploading(true);

      UserService.ChangeLogo(payload)
        .then((response) => {
          message.success('Logo actualizado exitosamente.');
          setIsLogoModalVisible(false);
          setLogoFile(null);

          if (response && response.data) {
            updateUserInLocalStorage(response.data);
          }

          if (onProfileUpdate) onProfileUpdate();
        })
        .catch(err => {
          const errorMessage = err.response?.data?.message || 'Error al subir el logo.';
          message.error(errorMessage);
        })
        .finally(() => setLogoUploading(false));
    };
    reader.onerror = () => message.error('Error al leer el archivo de imagen.');
  };

  const handleFiscalUpload = () => {
    if (!fiscalFile) return message.error('Por favor, selecciona un archivo PDF.');

    setFiscalUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(fiscalFile);

    reader.onload = () => {

      const payload = {
        fiscalBase64: reader.result,
        userId: profile.id,
      };

      UserService.UploadFiscal(payload)
        .then((response) => {
          message.success('Constancia Fiscal subida exitosamente.');
          setFiscalFile(null);
          if (onProfileUpdate) onProfileUpdate();
        })
        .catch((err) => {
          const errorMessage = err.response?.data?.message || 'Error al subir el archivo. Intente de nuevo.';
          message.error(errorMessage);
        })
        .finally(() => {
          setFiscalUploading(false);
        });
    };

    reader.onerror = () => {
      message.error('Error al leer el archivo. Por favor, intente seleccionarlo de nuevo.');
      setFiscalUploading(false);
    };
  };

  if (!profile) {
    return <div className="profile-container"><Skeleton active avatar={{ size: 180 }} paragraph={{ rows: 10 }} /></div>;
  }

  const displayData = isEditing ? editedProfile : profile;

  const renderDashboardButton = () => {
    if (String(displayData.role) === '1') {
      return (
        <Button icon={<DashboardOutlined />} onClick={() => navigate('/dashboard')} style={{ marginLeft: '8px' }}>
          Volver al dashboard
        </Button>
      );
    }
    return null;
  };

  const bannerUploadProps = {
    accept: ".jpg,.jpeg,.png",
    onRemove: () => setBannerFile(null),
    beforeUpload: file => { setBannerFile(file); return false; },
    fileList: bannerFile ? [bannerFile] : []
  };

  const logoUploadProps = {
    accept: ".jpg,.jpeg,.png",
    onRemove: () => setLogoFile(null),
    beforeUpload: file => { setLogoFile(file); return false; },
    fileList: logoFile ? [logoFile] : []
  };

  const fiscalUploadProps = {
    accept: ".pdf",
    onRemove: () => setFiscalFile(null),
    beforeUpload: file => { setFiscalFile(file); return false; },
    fileList: fiscalFile ? [fiscalFile] : []
  };

  const roleMapping = {
    1: 'Administrador',
    2: 'Finanzas',
    3: 'Compras',
    4: 'Cliente',
    5: 'Ventas',
    6: 'Proveedor',
    7: 'Repartidor'
  };

  const catalogUploadProps = { accept: "application/pdf", onRemove: () => setCatalogFile(null), beforeUpload: file => { setCatalogFile(file); return false; }, fileList: catalogFile ? [catalogFile] : [] };
  const certsUploadProps = { accept: "application/pdf", multiple: true, onRemove: (file) => { setCertFiles(prev => prev.filter(f => f.uid !== file.uid)); }, beforeUpload: file => { setCertFiles(prev => [...prev, file]); return false; }, fileList: certFiles };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });


  const handleOpenCatalog = async () => {
    if (!profileSupplier || !profileSupplier.id) return message.error('Catálogo no disponible');

    const maybeUrl = profileSupplier.catalogUrl || profileSupplier.catalogoUrl;
    if (maybeUrl && maybeUrl.includes('?')) {
      window.open(maybeUrl, '_blank');
      return;
    }

    try {
      const resp = await SupplierService.GetSupplierById(profileSupplier.id);
      const fresh = resp.data;

      const url = fresh.catalogUrl || fresh.catalogoUrl;
      if (url) {
        message.info('Abriendo catálogo...');
        window.open(url, '_blank');
      } else {
        message.error('No hay catálogo disponible.');
      }
    } catch (err) {
      console.error('Error fetching signed catalog url', err);
      message.error('No se pudo obtener el catálogo.');
    }
  };



  const roleText = roleMapping[displayData.role] || 'Unknown Role';
  const fullAddress = [displayData.colony, displayData.city ? displayData.city : '', displayData.state, displayData.country, displayData.postal_code].filter(Boolean).join(", ");
  const categoryMapping = { 1: 'MRO', 2: 'Empaque', 3: 'MRP', 4: 'Servicios', 5: 'Maquinado', 6: 'Tecnología' };
  const bannerImage = displayData.banner_img;
  const isSupplier = String(displayData.role) === '6' || displayData.role === 6;
  const currentUser = getCurrentUserSession();
  const isOwnProfile = String(currentUser.id) === String(displayData.id);
  const isAdminUser = String(currentUser.role) === '1';
  const canEditProfile = isOwnProfile || isAdminUser || profileSupplier?.typeSupplier === "REFERENCIADO";

  return (
    <div className="profile-container">
      <div className="profile-card-wrapper">
        <header className="profile-header">
          <div className="header-top-banner" onClick={() => setIsBannerModalVisible(true)}>
            {bannerImage && <img src={bannerImage} alt="User Banner" />}
            <div className="banner-edit-icon"><EditOutlined /></div>
          </div>
          <div className="profile-avatar-container" onClick={() => setIsLogoModalVisible(true)}>
            <Avatar size={180} src={displayData.logo} icon={!displayData.logo && <UserOutlined />} />
            <div className="avatar-edit-icon"><EditOutlined /></div>
          </div>
          <div className="header-bottom-banner">
            <div className="user-details">
              {isSupplier && (<h1>{profileSupplier?.bussiness}</h1>)}
              {!isSupplier && (<h1>{displayData.name || "Nombre de Usuario"}</h1>)}
              <p className="user-tag">{roleText}</p>
            </div>
            <div className="header-actions">
              {isEditing ? (
                <>
                  <Button icon={<SaveOutlined />} type="primary" onClick={handleSave}>
                    Guardar cambios
                  </Button>
                  <Button
                    icon={<CloseCircleOutlined />}
                    onClick={handleEditToggle}
                    style={{ marginLeft: "8px" }}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  {canEditProfile ? (
                    <Button icon={<EditOutlined />} onClick={handleEditToggle}>
                      Editar Perfil
                    </Button>
                  ) : null}
                </>
              )}
              {renderDashboardButton()}
            </div>
          </div>
        </header>
        {!isSupplier && (
          <main className="profile-body">
            <aside className="contact-info-card">
              <InfoItem icon={<UserOutlined />} text={displayData.role_name} isEditing={false} />
              <InfoItem icon={<HomeOutlined />} text={displayData.bussiness} isEditing={false} />
              <InfoItem icon={<CreditCardOutlined />} text={displayData.rfc} isEditing={false} />
              <InfoItem
                icon={<PhoneOutlined />}
                text={displayData.phone}
                isEditing={isEditing}
                value={editedProfile.phone || ''}
                onChange={(e) => handleInputChange(e, 'phone')}
              />
              <InfoItem icon={<MailOutlined />} text={displayData.email} isEditing={false} />
              <InfoItem icon={<EnvironmentOutlined />} text={fullAddress} isEditing={false} />
            </aside>

            <div className="details-grid">
              <div className="detail-card about-me-card">
                <h3>Acerca de mí:</h3>
                {isEditing ? (
                  <Input.TextArea
                    rows={4}
                    value={editedProfile.aboutMe || ''}
                    onChange={(e) => handleInputChange(e, 'aboutMe')}
                  />
                ) : (
                  <p>{displayData.aboutMe || 'Ejemplo de descripcion del usuario/perfil de empresa'}</p>
                )}
                <br />
                <h3>Constancia Fiscal</h3>
                <div>
                  <Upload {...fiscalUploadProps}>
                    <Button icon={<SafetyCertificateOutlined />}>Seleccionar PDF</Button>
                  </Upload>
                  <Button type="primary" onClick={handleFiscalUpload} disabled={!fiscalFile} loading={fiscalUploading} style={{ marginTop: 16 }}>
                    {fiscalUploading ? 'Subiendo...' : 'Subir Archivo'}
                  </Button>
                </div>
                {displayData.fiscalUrl && (
                  <Button type="link" onClick={() => window.open(displayData.fiscalUrl, '_blank')} style={{ marginTop: '10px', display: 'inline-block' }}>Ver constancia fiscal</Button>
                )}
              </div>
              <div className="detail-card certificate-card">
                <SafetyCertificateOutlined />
                <h3>Certificado</h3>
              </div>
              <div className="detail-card membership-card">
                <TrophyOutlined />
                <h3>Membresía</h3>
                <p>En curso</p>
                <p>Año de Inscripción: 2025</p>
              </div>
            </div>
          </main>
        )}
        {/* parte para proveedor */}
        {isSupplier && (
          <main className="profile-body">
            <aside className="contact-info-card">
              <InfoItem icon={<UserOutlined />} text={`Categoría: ${categoryMapping[profileSupplier?.category] || 'No disponible'}`} isEditing={false} />
              <InfoItem icon={<ApartmentOutlined />} text={`Subcategoría: ${profileSupplier?.subcategory || 'No disponible'}`} isEditing={false} />
              <InfoItem icon={<HomeOutlined style={{ width: 40 }} />} text={`Dirección: ${profileSupplier?.street || 'No disponible'}, ${profileSupplier?.city || ''} , ${profileSupplier?.state || 'No disponible'} , ${profileSupplier?.country || 'No disponible'}, `} isEditing={false} />
              <InfoItem icon={<IdcardOutlined />} text={`RFC: ${profileSupplier?.rfc || 'No disponible'}`} isEditing={false} />
              <InfoItem icon={<PercentageOutlined />} text={`IVA: ${profileSupplier?.iva * 100 || 'No disponible'}%`} isEditing={false} />
              <InfoItem icon={<GlobalOutlined style={{ width: 25 }} />} text={`Sitio Web: ${profileSupplier?.website || 'No disponible'}`} isEditing={false} />
              <InfoItem icon={<PayCircleOutlined />} text={`Días de crédito: ${profileSupplier?.credit_days || 'No disponible'}`} isEditing={false} />
              <Button type="primary" onClick={() => setIsContactsModalVisible(true)}>Contactos de Referencia</Button>

            </aside>

            <div className="details-grid">
              <div className="detail-card about-me-card">
                <h3>Descripción:</h3>
                {isEditing ? (
                  <Input.TextArea
                    rows={4}
                    value={editedProfile.aboutMe || ''}
                    onChange={(e) => handleInputChange(e, 'aboutMe')}
                  />
                ) : (
                  <p>{profileSupplier?.description || 'Describe de lo que se trata la empresa'}</p>
                )}
                <br />
                <h3>Constancia Fiscal</h3>

                <div>
                  <Row gutter={[24, 24]} style={{ marginTop: 8, marginBottom: 8 }} align="middle" justify="start" className="fiscal-buttons-row">
                    {displayData.fiscalUrl && (
                      <Col xs={8} sm="auto" className="fiscal-btn-col"  >
                        <Button onClick={() => window.open(displayData.fiscalUrl, '_blank')}>
                          Ver constancia fiscal
                        </Button>

                      </Col>
                    )}


                  </Row>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '16px', marginTop: '8px', width: '70%'  }}>
                  <Upload {...fiscalUploadProps}>
                    <Button type="primary">Seleccionar PDF</Button>
                  </Upload>

                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '26px', marginTop: '8px', width: '20%'  }}>
                <Button type="primary" onClick={handleFiscalUpload} disabled={!fiscalFile} loading={fiscalUploading}>
                  {fiscalUploading ? 'Subiendo...' : 'Subir Archivo'}
                </Button>
                </div>
              </div>
              <div className="detail-card certificate-card">
                <SafetyCertificateOutlined />
                <h3>Catálogo</h3>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {profileSupplier?.catalog && profileSupplier.catalog.length > 0 ? (
                    <>
                      {profileSupplier.catalog.map((cert) => (
                        <Button key={cert.id} onClick={() => window.open(cert.fileUrl, '_blank')}>{cert.fileName || cert.fileName}</Button>
                      ))}
                    </>
                  ) : (
                    <div>No hay catalogos cargados.</div>
                  )}
                </div>
              </div>

              <div className="detail-card membership-card">
                <TrophyOutlined />
                <h3>Certificados</h3>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {profileSupplier?.certificates && profileSupplier.certificates.length > 0 ? (
                    <>
                      {profileSupplier.certificates.map((cert) => (
                        <Button key={cert.id} onClick={() => handleOpenCertificate(cert)}>{cert.fileName || cert.fileName}</Button>
                      ))}
                    </>
                  ) : (
                    <div>No hay certificados cargados.</div>
                  )}
                </div>
              </div>
            </div>
          </main>
        )}
      </div>

      <Modal
        title="Contactos de Referencia"
        open={isContactsModalVisible}
        onCancel={() => setIsContactsModalVisible(false)}
        footer={null}
        width={520}
      >
        {profileSupplier && profileSupplier.contacts && profileSupplier.contacts.length > 0 ? (
          <div className="contacts-modal-list">
            {profileSupplier.contacts.map((c) => (
              <div className="contact-card" key={c.id || c.email || Math.random()}>
                <div className="contact-row" >
                  <div className="contact-icon">👤</div>
                  <div className="contact-text">
                    <div className="contact-name">{c.name || '—'}</div>
                    <div className="contact-position">{c.position || '—'}</div>
                  </div>
                </div>

                <div className="contact-row">
                  <div className="contact-icon">✉️</div>
                  <div className="contact-text"><a href={`mailto:${c.email}`}>{c.email || '—'}</a></div>
                </div>

                <div className="contact-row">
                  <div className="contact-icon">📱</div>
                  <div className="contact-text"> {(c.phone || 'N/A')}</div>
                </div>

                <div className="contact-row">
                  <div className="contact-icon">📠</div>
                  <div className="contact-text"> {('+ ' + c.mobile || 'N/A')}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>No hay contactos disponibles.</div>
        )}
      </Modal>

      <Modal
        title="Actualizar Imagen del Banner"
        open={isBannerModalVisible}
        onCancel={() => setIsBannerModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsBannerModalVisible(false)}>Cancelar</Button>,
          <Button key="submit" type="primary" loading={bannerUploading} onClick={handleBannerUpload}>
            {bannerUploading ? 'Subiendo...' : 'Actualizar'}
          </Button>,
        ]}
      >
        <p style={{ marginTop: -5, marginBottom: 15, fontSize: 13, color: 'rgba(0, 0, 0, 0.55)' }}>
          Tamaño sugerido: 1200x150 px. Formatos permitidos: PNG, JPG.
        </p>
        <Upload {...bannerUploadProps} listType="picture-card">
          <div>
            <UploadOutlined />
            <div style={{ marginTop: 8 }}>Seleccionar Imagen</div>
          </div>
        </Upload>
      </Modal>

      <Modal
        title="Actualizar Logo"
        open={isLogoModalVisible}
        onCancel={() => setIsLogoModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsLogoModalVisible(false)}>Cancelar</Button>,
          <Button key="submit" type="primary" loading={logoUploading} onClick={handleLogoUpload}>
            {logoUploading ? 'Subiendo...' : 'Actualizar'}
          </Button>,
        ]}
      >
        <p style={{ marginTop: -5, marginBottom: 15, fontSize: 13, color: 'rgba(0, 0, 0, 0.55)' }}>
          Tamaño sugerido: 400x400 px. Formatos permitidos: PNG, JPG.
        </p>
        <Upload {...logoUploadProps} listType="picture-card">
          <div>
            <UploadOutlined />
            <div style={{ marginTop: 8 }}>Seleccionar Imagen</div>
          </div>
        </Upload>
      </Modal>

      <Modal
        title={editingSupplierId ? "Editar perfil" : "Nuevo Perfil"}
        open={openCreateModal}
        onCancel={() => {
          setOpenCreateModal(false);
          setEditingSupplierId(null);
        }}
        footer={null}
        width={900}

      >
        <CreateSupplierForm
          supplierId={editingSupplierId}
          disabledFields={["bussiness", "category"]}
          onSuccess={async () => {
            setOpenCreateModal(false);
            setEditingSupplierId(null);
            if (onProfileUpdate) await onProfileUpdate();
          }}
        />
      </Modal>


    </div>
  );
}